import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminUserDto, AdminUsersResponse } from '@eskoolhub/types';
import { PageHeader } from '../components/AppShell';
import { IconChevron, IconPlus } from '../components/Icons';
import { useToast } from '../components/Toasts';
import { Chip, ErrorState, SkeletonRows, SkeletonTiles, StatTile } from '../components/ui';
import { AdminDetailModal } from '../features/AdminDetailModal';
import { InviteAdminModal } from '../features/InviteAdminModal';
import { useAuth } from '../auth/AuthContext';
import { api, ApiRequestError } from '../lib/api';
import { formatNumber, pluralise } from '../lib/format';

/** "Now", "2h ago", "Today 08:12", "Yesterday", "3d ago" — as in design A06. */
export function formatLastActive(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso);
  const minutes = Math.floor((Date.now() - then.getTime()) / 60000);

  if (minutes < 5) return 'Now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  const now = new Date();
  const isToday = then.toDateString() === now.toDateString();
  if (isToday) {
    return hours < 6
      ? `${hours}h ago`
      : `Today ${then.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (then.toDateString() === yesterday.toDateString()) return 'Yesterday';

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return then.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function StatusChip({ status }: { status: AdminUserDto['status'] }) {
  if (status === 'ACTIVE') return <Chip tone="success">Active</Chip>;
  if (status === 'INVITED') return <Chip tone="warn">Invited</Chip>;
  return <Chip tone="neutral">Inactive</Chip>;
}

export function AdminUsersPage() {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [selected, setSelected] = useState<AdminUserDto | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['platform-users'],
    queryFn: () => api.get<AdminUsersResponse>('/api/platform-users'),
  });

  const resend = useMutation({
    mutationFn: (id: string) => api.post<{ invite: { email: string } }>(`/api/platform-users/${id}/resend`),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['platform-users'] });
      toast.push(`Invite resent to ${result.invite.email}`, 'success');
    },
    onError: (err) => toast.push(err instanceof ApiRequestError ? err.message : 'Could not resend', 'error'),
  });

  return (
    <>
      <PageHeader
        title="Admin users"
        meta={data ? `${pluralise(data.total, 'platform-level account')}` : 'Loading…'}
        actions={
          <button className="btn" onClick={() => setInviting(true)}>
            <IconPlus fill="var(--ink)" />
            Invite admin
          </button>
        }
      />

      <div className="page">
        {error ? (
          <ErrorState
            message={error instanceof ApiRequestError ? error.message : 'Could not load admin users'}
            onRetry={() => void refetch()}
          />
        ) : isPending ? (
          <>
            <SkeletonTiles count={3} />
            <SkeletonRows count={5} />
          </>
        ) : (
          <>
            <div className="grid-3">
              <StatTile
                label="Total admins"
                value={formatNumber(data.total)}
                caption={`${data.superAdminCount} super admin · ${data.supportCount} support`}
              />
              <StatTile
                label="2FA enabled"
                value={`${data.twoFactorEnabled}/${data.twoFactorEligible}`}
                caption="Enforced for all roles"
                tone={data.twoFactorEnabled === data.twoFactorEligible ? 'success' : 'danger'}
              />
              <StatTile
                label="Pending invites"
                value={formatNumber(data.pendingInvites)}
                caption={
                  data.oldestPendingInviteAt
                    ? `Sent ${formatLastActive(data.oldestPendingInviteAt).replace(' ago', ' ago')}`
                    : 'none outstanding'
                }
                tone={data.pendingInvites > 0 ? 'warn' : undefined}
              />
            </div>

            <div className="card card--flush">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th style={{ width: 240 }}>Role</th>
                    <th style={{ width: 150 }}>Last active</th>
                    <th style={{ width: 130 }}>Status</th>
                    <th style={{ width: 90 }} />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((admin) => {
                    const isInvite = admin.status === 'INVITED';
                    const isSelf = admin.id === user?.id;
                    return (
                      <tr key={admin.id} onClick={() => setSelected(admin)}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                            <div
                              className="avatar"
                              style={{
                                width: 36,
                                height: 36,
                                background: isInvite ? 'var(--ink-tint)' : admin.role === 'SUPER_ADMIN' ? 'var(--ink)' : 'var(--ink-soft)',
                                color: isInvite ? 'var(--ink-soft)' : 'var(--marigold)',
                                fontSize: 12,
                              }}
                            >
                              {isInvite ? '✉' : admin.initials}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                              <span
                                style={{ fontSize: 14.5, fontWeight: 700, color: isInvite ? 'var(--ink-soft)' : undefined }}
                              >
                                {isInvite ? admin.email : admin.name}
                                {isSelf ? <span className="tag" style={{ marginLeft: 8 }}>You</span> : null}
                              </span>
                              <span className="mono" style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>
                                {isInvite
                                  ? `Invited ${formatLastActive(admin.invitedAt)}`
                                  : admin.email}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td style={{ fontSize: 14, fontWeight: 500, color: isInvite ? 'var(--ink-soft)' : undefined }}>
                          {admin.roleLabel}
                        </td>
                        <td className="mono" style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
                          {formatLastActive(admin.lastActiveAt)}
                        </td>
                        <td>
                          <StatusChip status={admin.status} />
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {isInvite ? (
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                resend.mutate(admin.id);
                              }}
                              disabled={resend.isPending}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 700,
                                color: 'var(--marigold-deep)',
                                fontFamily: 'var(--font-body)',
                              }}
                            >
                              Resend
                            </button>
                          ) : (
                            <IconChevron fill="var(--ink-soft)" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="card">
              <div className="card__title">Roles</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                <div style={{ border: '1px solid var(--rule)', borderRadius: 16, padding: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Super admin</div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink-soft)' }}>
                    Full access: onboarding, billing, plan changes, suspending schools, managing other admins.
                  </div>
                </div>
                <div style={{ border: '1px solid var(--rule)', borderRadius: 16, padding: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Support</div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink-soft)' }}>
                    Tickets and read-only school data by default; billing access can be granted per admin.
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {inviting ? <InviteAdminModal onClose={() => setInviting(false)} /> : null}
      {selected ? (
        <AdminDetailModal admin={selected} isSelf={selected.id === user?.id} onClose={() => setSelected(null)} />
      ) : null}
    </>
  );
}
